import * as THREE from 'three/webgpu';
import type { FeatureFace } from '../../mesh/feature';
import {
    MeasureDistancePrimitiveKind,
    type MeasureDistanceGeometry,
    type MeasureNormalDistanceResult,
} from '../types/measure-distance-types';
import { computeDistance3DMillimeter } from '../math/MeasureNumericKernel';
import { resolveBoundedPlanarFaceWorldPlane } from './measureBoundedPlanarFace';

/** 垂直距离与最短距离相等判定容差（毫米）。 */
const MEASURE_NORMAL_DISTANCE_EQUAL_EPSILON_MM = 0.001;

/** 退化线段/方向判定容差。 */
const MEASURE_NORMAL_DISTANCE_DEGENERATE_EPSILON = 1e-9;

/** 正交辅助线可渲染长度容差（世界单位）。 */
const MEASURE_TRANSVERSE_LINE_LENGTH_EPSILON = 1e-9;

/** 正交辅助线超出有限最近点的延伸比例（相对垂足→最近点段长）。 */
const MEASURE_NORMAL_DISTANCE_TRANSVERSE_EXTEND_RATIO = 0.25;

/** 正交辅助线最小延伸长度（世界单位）。 */
const MEASURE_NORMAL_DISTANCE_TRANSVERSE_MIN_EXTEND = 0.1;

/**
 * 基于归一化几何体求解点到目标体的垂直距离。
 *
 * 设计说明：
 * 1. 仅支持「点 + 直线/平面」组合，对齐 SolidWorks Normal Distance 语义；
 * 2. 目标体按无限延伸直线/平面求垂足，再与全局最短距离比较决定是否展示；
 * 3. 正交辅助线从有限体最近点经垂足再沿同向延伸一段（与垂直距离线正交）；
 * 4. 圆弧折线、非平面曲面等不支持垂直距离，返回 null。
 *
 * @param point 查询点（世界坐标）。
 * @param target 目标几何体（有限线段、无限直线、无限平面或有边界平面面）。
 * @param closestDistance 已求得的全球最短距离，用于判断是否需要展示垂直距离。
 * @param boundedClosestOnTarget 目标有限体上的全局最近点（辅助线必经点）。
 * @returns 垂直距离结果；不支持或无法计算时返回 null。
 */
export function resolveNormalDistanceBetweenGeometries(
    point: THREE.Vector3,
    target: MeasureDistanceGeometry,
    closestDistanceMillimeter: number,
    boundedClosestOnTarget: THREE.Vector3,
): MeasureNormalDistanceResult | null {
    switch (target.kind) {
        case MeasureDistancePrimitiveKind.Segment:
            return resolvePointToSegmentNormalDistance(
                point,
                target.start,
                target.end,
                closestDistanceMillimeter,
                boundedClosestOnTarget,
            );
        case MeasureDistancePrimitiveKind.InfiniteLine:
            return resolvePointToInfiniteLineNormalDistance(
                point,
                target.origin,
                target.direction,
                closestDistanceMillimeter,
                boundedClosestOnTarget,
            );
        case MeasureDistancePrimitiveKind.Plane:
            return resolvePointToInfinitePlaneNormalDistance(
                point,
                target.point,
                target.normal,
                closestDistanceMillimeter,
                boundedClosestOnTarget,
            );
        case MeasureDistancePrimitiveKind.MeshFace:
            return resolvePointToBoundedPlanarFaceNormalDistance(
                point,
                target.face,
                closestDistanceMillimeter,
                boundedClosestOnTarget,
            );
        default:
            return null;
    }
}

/**
 * 求点到有限线段的垂直距离（沿无限延伸直线）。
 *
 * @param point 查询点。
 * @param start 线段起点。
 * @param end 线段终点。
 * @param closestDistance 全局最短距离。
 * @param boundedClosestOnTarget 线段上的全局最近点。
 * @returns 垂直距离结果。
 */
function resolvePointToSegmentNormalDistance(
    point: THREE.Vector3,
    start: THREE.Vector3,
    end: THREE.Vector3,
    closestDistanceMillimeter: number,
    boundedClosestOnTarget: THREE.Vector3,
): MeasureNormalDistanceResult | null {
    const direction = end.clone().sub(start);
    if (direction.lengthSq() <= MEASURE_NORMAL_DISTANCE_DEGENERATE_EPSILON) {
        return null;
    }
    direction.normalize();

    const foot = closestPointOnInfiniteLine(point, start, direction);
    return buildNormalDistanceResult(point, foot, boundedClosestOnTarget, closestDistanceMillimeter);
}

/**
 * 求点到无限直线的垂直距离。
 *
 * @param point 查询点。
 * @param origin 直线上一点。
 * @param direction 单位方向。
 * @param closestDistance 全局最短距离。
 * @param boundedClosestOnTarget 直线上的全局最近点。
 * @returns 垂直距离结果。
 */
function resolvePointToInfiniteLineNormalDistance(
    point: THREE.Vector3,
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    closestDistanceMillimeter: number,
    boundedClosestOnTarget: THREE.Vector3,
): MeasureNormalDistanceResult | null {
    if (direction.lengthSq() <= MEASURE_NORMAL_DISTANCE_DEGENERATE_EPSILON) {
        return null;
    }

    const foot = closestPointOnInfiniteLine(point, origin, direction);
    return buildNormalDistanceResult(point, foot, boundedClosestOnTarget, closestDistanceMillimeter);
}

/**
 * 求点到无限平面的垂直距离。
 *
 * @param point 查询点。
 * @param planePoint 平面上一点。
 * @param planeNormal 单位法向。
 * @param closestDistance 全局最短距离。
 * @param boundedClosestOnTarget 平面上的全局最近点。
 * @returns 垂直距离结果。
 */
function resolvePointToInfinitePlaneNormalDistance(
    point: THREE.Vector3,
    planePoint: THREE.Vector3,
    planeNormal: THREE.Vector3,
    closestDistanceMillimeter: number,
    boundedClosestOnTarget: THREE.Vector3,
): MeasureNormalDistanceResult | null {
    if (planeNormal.lengthSq() <= MEASURE_NORMAL_DISTANCE_DEGENERATE_EPSILON) {
        return null;
    }

    const foot = projectPointOntoPlane(point, planePoint, planeNormal);
    return buildNormalDistanceResult(point, foot, boundedClosestOnTarget, closestDistanceMillimeter);
}

/**
 * 求点到有边界平面面的垂直距离（沿面所在无限平面法向）。
 *
 * @param point 查询点。
 * @param face 面代理。
 * @param closestDistance 全局最短距离。
 * @param boundedClosestOnTarget 面上的全局最近点。
 * @returns 垂直距离结果；非平面面时返回 null。
 */
function resolvePointToBoundedPlanarFaceNormalDistance(
    point: THREE.Vector3,
    face: FeatureFace,
    closestDistanceMillimeter: number,
    boundedClosestOnTarget: THREE.Vector3,
): MeasureNormalDistanceResult | null {
    const worldPlane = resolveBoundedPlanarFaceWorldPlane(face);
    if (!worldPlane) {
        return null;
    }

    const foot = projectPointOntoPlane(point, worldPlane.point, worldPlane.normal);
    return buildNormalDistanceResult(point, foot, boundedClosestOnTarget, closestDistanceMillimeter);
}

/**
 * 组装垂直距离结果并判定是否应展示垂线与正交辅助线。
 *
 * @param point 点侧端点。
 * @param foot 无限几何垂足。
 * @param boundedClosestOnTarget 有限体上的全局最近点。
 * @param closestDistance 全局最短距离。
 * @returns 垂直距离结果。
 */
function buildNormalDistanceResult(
    point: THREE.Vector3,
    foot: THREE.Vector3,
    boundedClosestOnTarget: THREE.Vector3,
    closestDistanceMillimeter: number,
): MeasureNormalDistanceResult {
    const distance = computeDistance3DMillimeter(
        point.x,
        point.y,
        point.z,
        foot.x,
        foot.y,
        foot.z,
    );
    const shouldRender = Math.abs(distance - closestDistanceMillimeter) > MEASURE_NORMAL_DISTANCE_EQUAL_EPSILON_MM;
    const transverseLine = resolveTransverseLineEndpoints(foot, boundedClosestOnTarget);
    const transverseFrom = transverseLine?.from ?? boundedClosestOnTarget.clone();
    const transverseTo = transverseLine?.to ?? foot.clone();
    const shouldRenderTransverse = shouldRender
        && transverseLine !== null
        && hasSegmentLength(transverseFrom, transverseTo);

    return {
        from: point.clone(),
        to: foot.clone(),
        distance,
        shouldRender,
        transverseFrom,
        transverseTo,
        shouldRenderTransverse,
    };
}

/**
 * 正交辅助线端点。
 */
interface TransverseLineEndpoints {
    /** 辅助线起点（有限体最近点）。 */
    from: THREE.Vector3;
    /** 辅助线终点（垂足后再沿远离最近点方向延伸）。 */
    to: THREE.Vector3;
}

/**
 * 计算正交辅助线端点：最近点 → 垂足 → 同向延伸一段。
 *
 * 设计说明：对齐 SolidWorks 临时图形——辅助线从有限体最近点出发，
 * 经过与垂直距离线相连的垂足，再沿远离最近点方向继续延伸。
 *
 * @param foot 垂足（与垂直距离线终点重合）。
 * @param boundedClosestOnTarget 有限体上的全局最近点（辅助线起点）。
 * @returns 辅助线端点；垂足与最近点重合时返回 null。
 */
function resolveTransverseLineEndpoints(
    foot: THREE.Vector3,
    boundedClosestOnTarget: THREE.Vector3,
): TransverseLineEndpoints | null {
    const closestToFoot = foot.clone().sub(boundedClosestOnTarget);
    const closestToFootLength = closestToFoot.length();
    if (closestToFootLength <= MEASURE_TRANSVERSE_LINE_LENGTH_EPSILON) {
        return null;
    }

    closestToFoot.normalize();
    const extensionLength = Math.max(
        MEASURE_NORMAL_DISTANCE_TRANSVERSE_MIN_EXTEND,
        closestToFootLength * MEASURE_NORMAL_DISTANCE_TRANSVERSE_EXTEND_RATIO,
    );
    const extendedEnd = foot.clone().addScaledVector(closestToFoot, extensionLength);

    return {
        from: boundedClosestOnTarget.clone(),
        to: extendedEnd,
    };
}

/**
 * 将点投影到无限延伸平面。
 *
 * @param point 待投影点。
 * @param planePoint 平面上一点。
 * @param planeNormal 单位法向。
 * @returns 投影点。
 */
function projectPointOntoPlane(
    point: THREE.Vector3,
    planePoint: THREE.Vector3,
    planeNormal: THREE.Vector3,
): THREE.Vector3 {
    const offset = point.clone().sub(planePoint);
    const signedDistance = offset.dot(planeNormal);
    return point.clone().addScaledVector(planeNormal, -signedDistance);
}

/**
 * 求点到无限直线的垂足。
 *
 * @param point 查询点。
 * @param origin 直线上一点。
 * @param direction 单位方向。
 * @returns 直线上的垂足。
 */
function closestPointOnInfiniteLine(
    point: THREE.Vector3,
    origin: THREE.Vector3,
    direction: THREE.Vector3,
): THREE.Vector3 {
    const offset = point.clone().sub(origin);
    const parameter = offset.dot(direction);
    return origin.clone().addScaledVector(direction, parameter);
}

/**
 * 判断线段是否具有可渲染长度。
 *
 * @param start 起点。
 * @param end 终点。
 * @returns 长度大于容差时返回 true。
 */
function hasSegmentLength(start: THREE.Vector3, end: THREE.Vector3): boolean {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const dz = end.z - start.z;
    return dx * dx + dy * dy + dz * dz
        > MEASURE_TRANSVERSE_LINE_LENGTH_EPSILON * MEASURE_TRANSVERSE_LINE_LENGTH_EPSILON;
}

